// @ts-nocheck
import useLog from '../useLog';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';

const OAUTH_STATE_KEY = 'sgDiscordOAuthState';

export type DiscordRoleStatus = 'ok' | 'none' | 'error';

export interface LinkDiscordRsp {
  error?: string;
  discordUser?: { id: string; username: string; globalName: string | null };
  subscription?: 'Alpha' | 'Pro' | 'Standard' | null;
  roleStatus?: DiscordRoleStatus;
}

// A fresh random `state` for each Discord login, kept for this tab only. Discord
// echoes it back with the code, and `discordOAuthStateMatches` checks it.
export const newDiscordOAuthState = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const state = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(
    '',
  );
  try {
    sessionStorage.setItem(OAUTH_STATE_KEY, state);
  } catch (_err) {
    // Storage blocked: the returning code will fail the state check below
  }
  return state;
};

// True when `returned` is the state this tab generated for its last login.
export const discordOAuthStateMatches = (returned: string | null): boolean => {
  let expected: string | null = null;
  try {
    expected = sessionStorage.getItem(OAUTH_STATE_KEY);
  } catch (_err) {
    // Storage blocked: treat as a mismatch
  }
  return expected != null && returned != null && expected === returned;
};

export const clearDiscordOAuthState = () => {
  try {
    sessionStorage.removeItem(OAUTH_STATE_KEY);
  } catch (_err) {
    // Nothing to clear
  }
};

// Hands the code Discord sent back to the server, which links the Discord
// account to the signed-in user and assigns their server role in one step.
export const useLinkDiscord = <TData = LinkDiscordRsp, TError = string>(
  {
    code,
    redir,
  }: {
    code?: string | null;
    redir?: string;
  },
  options?: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'>,
) => {
  const { fetchAPIWithLog } = useLog('useLinkDiscord');
  return useQuery<TData, TError>({
    queryKey: ['useLinkDiscord', { code, redir }],
    queryFn: async (): Promise<TData> =>
      await fetchAPIWithLog<TData>('v1/me/discord/link', {
        method: 'POST',
        body: JSON.stringify({ code, redir }),
      }),
    enabled: code != null && redir != null && redir !== '',
    // A Discord code works once, so never resend it
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    ...options,
  });
};
